import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Heart, Shield, Search, ArrowRight, CheckCircle, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import logoImg from '../assets/logo.png';

export default function Landing() {
  const { isAuthenticated } = useAuth();

  const features = [
    {
      icon: <Users className="w-6 h-6" />,
      title: "Perfiles detallados",
      description: "Conoce los hábitos, preferencias y estilo de vida de cada persona antes de contactar."
    },
    {
      icon: <Heart className="w-6 h-6" />,
      title: "Compatibilidad real",
      description: "Encuentra personas afines según tus preferencias de convivencia, horarios y hábitos."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Contacto seguro",
      description: "Solo compartimos tu contacto cuando hay interés mutuo o tú lo permites."
    },
    {
      icon: <Search className="w-6 h-6" />,
      title: "Filtros inteligentes",
      description: "Filtra por ciudad, presupuesto, fecha de entrada, mascotas y más."
    }
  ];

  const steps = [
    { number: "1", title: "Crea tu perfil", description: "Cuéntanos sobre ti, tus hábitos y preferencias de convivencia." },
    { number: "2", title: "Explora perfiles", description: "Descubre personas compatibles en tu ciudad y rango de presupuesto." },
    { number: "3", title: "Muestra interés", description: "Si alguien te encaja, muestra interés. Si es mutuo, ¡conectáis!" }
  ];

  return (
    <div className="min-h-screen bg-[hsl(40,33%,97%)]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[hsl(40,50%,99%)]/90 backdrop-blur-xl border-b border-[hsl(35,15%,90%)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2.5" data-testid="logo">
              <img src={logoImg} alt="Partykatu" className="h-9 w-auto" />
              <span className="font-heading font-semibold text-lg text-[hsl(144,18%,38%)] tracking-tight hidden sm:inline" translate="no">Partykatu</span>
            </Link>
            <div className="flex items-center gap-4">
              {isAuthenticated ? (
                <Link to="/feed">
                  <Button className="bg-[hsl(144,18%,45%)] hover:bg-[hsl(144,18%,38%)] text-white rounded-full" data-testid="go-to-feed-btn">
                    Ir al feed
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/login" className="text-[hsl(20,10%,40%)] hover:text-[hsl(20,10%,27%)] font-medium hidden sm:block" data-testid="login-link">
                    Iniciar sesión
                  </Link>
                  <Link to="/register">
                    <Button className="bg-[hsl(144,18%,45%)] hover:bg-[hsl(144,18%,38%)] text-white rounded-full" data-testid="register-btn">
                      Registrarse
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[hsl(35,25%,94%)] -z-10"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 lg:py-36">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="text-center lg:text-left animate-fade-in">
              <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-[hsl(20,10%,27%)] tracking-tight leading-tight">
                Convivir bien
                <span className="text-[hsl(144,18%,45%)]"> empieza aquí</span>
              </h1>
              <p className="mt-4 text-base sm:text-lg font-medium text-[hsl(144,18%,40%)] tracking-wide">
                Filtra por estilo de vida y zona para convivir mejor
              </p>
              <p className="mt-8 text-lg sm:text-xl text-[hsl(20,8%,46%)] leading-relaxed max-w-xl mx-auto lg:mx-0">
                Conectamos personas que buscan compartir piso según compatibilidad de convivencia.
                Sin algoritmos complejos, solo perfiles reales y contacto directo.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link to="/register">
                  <Button className="w-full sm:w-auto bg-[hsl(144,18%,45%)] hover:bg-[hsl(144,18%,38%)] text-white rounded-full px-8 py-4 text-lg font-medium" data-testid="hero-register-btn">
                    Empezar gratis
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/login">
                  <Button variant="outline" className="w-full sm:w-auto rounded-full px-8 py-4 text-lg font-medium border-[hsl(35,15%,80%)] text-[hsl(20,10%,27%)] hover:bg-[hsl(35,25%,91%)]" data-testid="hero-login-btn">
                    Ya tengo cuenta
                  </Button>
                </Link>
              </div>
              <div className="mt-10 flex items-center gap-6 justify-center lg:justify-start text-sm text-[hsl(20,8%,46%)]">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[hsl(144,18%,45%)]" />
                  <span>100% gratis</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[hsl(144,18%,45%)]" />
                  <span>Sin suscripciones</span>
                </div>
              </div>
            </div>
            <div className="relative animate-fade-in stagger-2">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/c0e76bc7-b3ac-4012-a53d-3793af9fe6c4/images/a0e84d1aaa1ef11d42f35ffd58b3a90d7d5bb5c28b430f814579ddedcf9a68eb.png"
                alt="Personas mudándose a un apartamento"
                className="w-full max-w-lg mx-auto rounded-2xl shadow-2xl"
                data-testid="hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 sm:py-28 bg-[hsl(40,50%,99%)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl text-[hsl(20,10%,27%)]">
              ¿Por qué usar Partykatu?
            </h2>
            <p className="mt-4 text-[hsl(20,8%,46%)] max-w-2xl mx-auto">
              Diseñado para que encuentres personas compatibles de forma sencilla y segura
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-sm border border-[hsl(35,15%,90%)] hover:shadow-md transition-shadow duration-300 animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`feature-card-${index}`}
              >
                <div className="w-12 h-12 bg-[hsl(144,18%,90%)] rounded-xl flex items-center justify-center text-[hsl(144,18%,40%)] mb-5">
                  {feature.icon}
                </div>
                <h3 className="font-heading font-semibold text-lg text-[hsl(20,10%,27%)] mb-3">{feature.title}</h3>
                <p className="text-[hsl(20,8%,46%)] text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 sm:py-28 bg-[hsl(35,25%,94%)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl text-[hsl(20,10%,27%)]">
              Cómo funciona
            </h2>
            <p className="mt-4 text-[hsl(20,8%,46%)] max-w-2xl mx-auto">
              En tres simples pasos estarás conectando con personas afines
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {steps.map((step, index) => (
              <div key={index} className="text-center animate-fade-in" style={{ animationDelay: `${index * 0.15}s` }} data-testid={`step-${index}`}>
                <div className="w-16 h-16 bg-[hsl(144,18%,45%)] rounded-full flex items-center justify-center text-white font-heading font-bold text-2xl mx-auto mb-5">
                  {step.number}
                </div>
                <h3 className="font-heading font-semibold text-xl text-[hsl(20,10%,27%)] mb-3">{step.title}</h3>
                <p className="text-[hsl(20,8%,46%)]">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 sm:py-28 bg-[hsl(144,18%,45%)]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl text-white mb-8">
            ¿Listo para encontrar tu compañero de piso ideal?
          </h2>
          <p className="text-[hsl(144,18%,85%)] text-lg mb-10 max-w-2xl mx-auto">
            Únete gratis y empieza a explorar perfiles de personas que buscan compartir piso en tu ciudad.
          </p>
          <Link to="/register">
            <Button className="bg-white hover:bg-[hsl(35,25%,95%)] text-[hsl(144,18%,35%)] rounded-full px-8 py-4 text-lg font-medium" data-testid="cta-register-btn">
              Crear mi perfil gratis
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 bg-[hsl(20,10%,20%)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2.5">
              <img src={logoImg} alt="Partykatu" className="h-8 w-auto" />
              <span className="font-heading font-semibold text-white tracking-tight" translate="no">Partykatu</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-sm">
              <Link to="/privacidad" className="text-[hsl(20,8%,60%)] hover:text-white transition-colors">
                Política de Privacidad
              </Link>
              <Link to="/terminos" className="text-[hsl(20,8%,60%)] hover:text-white transition-colors">
                Términos y Condiciones
              </Link>
              <Link to="/aviso-legal" className="text-[hsl(20,8%,60%)] hover:text-white transition-colors">
                Aviso Legal
              </Link>
              <a href="mailto:infopartykatu@gmail.com" className="text-[hsl(20,8%,60%)] hover:text-white transition-colors">
                infopartykatu@gmail.com
              </a>
            </div>
          </div>
          <div className="mt-6 border-t border-[hsl(20,8%,30%)] pt-6 text-center">
            <p className="text-[hsl(20,8%,50%)] text-sm">
              © 2026 Partykatu · Alba Pérez Rodríguez · NIF 72596176B · Manuel Arroyo González · NIF 76269479F
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
